/**
 * Utils compatible with Edge runtime
 *
 * The Edge Runtime has some restrictions including:
 * Native Node.js APIs are not supported. For example, you can't read or write to the filesystem.
 * node_modules can be used, as long as they implement ES Modules and do not use native Node.js APIs.
 * Calling require directly is not allowed. Use ES Modules instead.
 * https://nextjs.org/docs/app/api-reference/edge
 */

import type { OperationVariables } from "@apollo/client/core";
import type { FormattedExecutionResult } from "graphql";
import { logger } from "@portal/app/_common/lib/logger";
import fetchRetry, { RequestInitRetryParams } from "fetch-retry";
import { HEADER_KEYS } from "@portal/app/_common/lib/headers";

/**
 * The RENDER_GIT_BRANCH variable doesn't get populated in the browser, but it does in the next.config.js at build time.
 * Since this is assigned to DREAM_ACTIVE_BRANCH in the config, NextJS actually surfaces DREAM_ACTIVE_BRANCH in the browser.
 * We still use RENDER_GIT_BRANCH for when it's used on the backend.
 */
export const isProduction = () =>
  (process.env.GIT_BRANCH ?? process.env.RENDER_GIT_BRANCH ?? process.env.DREAM_ACTIVE_BRANCH) ===
  "production";

export const getEnvironmentName = () => {
  const branch =
    process.env.GIT_BRANCH ?? process.env.RENDER_GIT_BRANCH ?? process.env.DREAM_ACTIVE_BRANCH;
  return !branch || branch === "local"
    ? "development"
    : branch === "develop"
      ? // Integration and staging both deploy the `develop` branch, so the
        // branch alone can't tell them apart. DREAM_ENV (a per-environment
        // Doppler var) is the discriminator that works on the server at
        // runtime. DREAM_PORTAL_URL cannot be used here: next.config's `env:`
        // block makes Next inline its BUILD-time value into the compiled
        // bundle, and the shared develop image is built with the staging
        // Doppler config — so on the integration deployment the inlined URL
        // still says staging (ENG-7545).
        process.env.DREAM_ENV === "integration"
        ? "integration"
        : "staging"
      : branch;
};

const graphqlUrlInternal = process.env.DREAM_INTERNAL_PORTAL_URL
  ? `${process.env.DREAM_INTERNAL_PORTAL_URL}:${process.env.PORT}/api/graphql`
  : `${process.env.DREAM_PORTAL_URL}/api/graphql`;

export type GQLFetchOptions = {
  clientName: string;
  accessToken?: string;
  refreshToken?: string;
  headers?: Record<string, string>;
  retry?: RequestInitRetryParams<typeof fetch>;
};

export const gqlFetch = async <
  Result extends Record<string, unknown>,
  Variables extends OperationVariables = OperationVariables,
>(
  body: { operationName: string; query: string; variables?: Variables },
  { clientName, accessToken, refreshToken, headers, retry }: GQLFetchOptions
): Promise<FormattedExecutionResult<Result>> => {
  try {
    const url = `${graphqlUrlInternal}${body.operationName ? `?${body.operationName}` : ""}`;
    const response = await fetchRetry(fetch)(url, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...(accessToken && {
          [HEADER_KEYS.AUTHORIZATION]: `Bearer ${accessToken}`,
        }),
        ...(refreshToken && {
          [HEADER_KEYS.REFRESH_TOKEN]: refreshToken,
        }),
        ...(clientName && {
          [HEADER_KEYS.APOLLO_CLIENT_NAME]: clientName,
        }),
        ...(process.env.DREAM_ACTIVE_VERSION && {
          [HEADER_KEYS.APOLLO_CLIENT_VERSION]: process.env.DREAM_ACTIVE_VERSION,
        }),
        ...headers,
      },
      body: JSON.stringify(body),
      ...retry,
    });

    // TODO: Remove after preview environment debugging
    if (!response.ok || !response.headers.get("content-type")?.includes("json")) {
      const text = await response.text();
      logger.error(
        {
          url,
          status: response.status,
          contentType: response.headers.get("content-type"),
          bodyPreview: text.slice(0, 500),
        },
        "gqlFetch: unexpected response"
      );
      throw new SyntaxError(
        `Expected JSON from ${url}, got ${response.status} ${response.headers.get("content-type")}`
      );
    }

    const result = (await response.json()) as FormattedExecutionResult<Result>;

    if (result.errors?.length && !isProduction()) {
      logger.error(result.errors, "gqlFetch returned with errors");
    }

    return result as FormattedExecutionResult<Result>;
  } catch (error) {
    if (!isProduction()) {
      logger.error(error, "Error occurred executing gqlFetch");
    }
    throw error;
  }
};
